import type { ReactNode } from "react";
import { AlertTriangle, Inbox, Loader2, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";

export function Spinner({ className, label }: { className?: string; label?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-2 text-sm text-muted-foreground", className)} role="status">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label ?? "Laden…"}
    </span>
  );
}

export function LoadingState({ label, className }: { label?: string; className?: string }) {
  return (
    <div className={cn("flex min-h-32 items-center justify-center p-8", className)}>
      <Spinner label={label} />
    </div>
  );
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
}: {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 px-6 py-10 text-center", className)}>
      <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
        {icon ?? <Inbox className="h-5 w-5" />}
      </div>
      <p className="font-display text-sm font-semibold">{title}</p>
      {description && <p className="max-w-md text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = "Er ging iets mis",
  message,
  onRetry,
  offline,
  className,
}: {
  title?: string;
  message?: ReactNode;
  onRetry?: () => void;
  /** Use the "not reachable" icon (connection/404 problems). */
  offline?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 px-6 py-10 text-center", className)} role="alert">
      <div className="mb-1 flex h-11 w-11 items-center justify-center rounded-full bg-[#FDECEC] text-destructive">
        {offline ? <WifiOff className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
      </div>
      <p className="font-display text-sm font-semibold">{title}</p>
      {message && <p className="max-w-md text-sm text-muted-foreground">{message}</p>}
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
          Opnieuw proberen
        </Button>
      )}
    </div>
  );
}

/** Inline banner for form-level success/error messages. */
export function Alert({
  tone = "danger",
  children,
  onClose,
  className,
}: {
  tone?: "danger" | "success" | "info" | "warning";
  children: ReactNode;
  onClose?: () => void;
  className?: string;
}) {
  const tones = {
    danger: "bg-[#FDECEC] border-[#F6C4C6] text-destructive",
    success: "bg-[#EEF6E4] border-[#CFE3B4] text-success",
    info: "bg-[#E8F0FA] border-[#C4D6EE] text-info",
    warning: "bg-sl-yellow-soft border-sl-yellow/60 text-sl-charcoal",
  };
  return (
    <div className={cn("flex items-start justify-between gap-3 rounded-md border px-4 py-3 text-sm", tones[tone], className)} role={tone === "danger" ? "alert" : "status"}>
      <div className="min-w-0">{children}</div>
      {onClose && (
        <button onClick={onClose} className="shrink-0 text-xs font-medium underline-offset-2 hover:underline" aria-label="Sluiten">
          Sluiten
        </button>
      )}
    </div>
  );
}
